import { useGetTaxonomyChildren } from '@/hooks/api/taxonomy/useGetTaxonomyChildren';
import { useInfiniteTreeScroll } from '@/hooks/useInfiniteTreeScroll';
import { memo, useEffect, useMemo } from 'react';
import { TaxonomyLazyTreeItem } from './TaxonomyLazyTreeItem';
import { LoadingIndicator } from './LoadingIndicator';
import type { TaxonomyTreeProperties } from './taxonomyTreeConstants';

const TaxonomyNodeLevelComponent = ({
  parentNodeId,
  depth,
  ...treeProperties
}: { parentNodeId: string; depth: number } & TaxonomyTreeProperties) => {
  const { nodes, hasNextPage, isFetchingNextPage, fetchNextPage } = useGetTaxonomyChildren(
    treeProperties.taxonomyId,
    parentNodeId
  );

  const { sentinelRef } = useInfiniteTreeScroll(treeProperties.scrollContainerReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  useEffect(() => {
    if (treeProperties.expandAllActive && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [treeProperties.expandAllActive, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const visibleNodes = useMemo(
    () => (treeProperties.searchVisibleIds ? nodes.filter(n => treeProperties.searchVisibleIds!.has(n.id)) : nodes),
    [nodes, treeProperties.searchVisibleIds]
  );

  return (
    <>
      {visibleNodes.map(node => (
        <TaxonomyLazyTreeItem key={node.id} node={node} depth={depth} {...treeProperties} />
      ))}
      {hasNextPage && <div ref={sentinelRef} style={{ height: 1 }} />}
      {isFetchingNextPage && <LoadingIndicator count={2} />}
    </>
  );
};

export const TaxonomyNodeLevel = memo(TaxonomyNodeLevelComponent);
